// src/pages/warehouse/WarehouseDialog.tsx

import React, { useEffect, useState, useMemo } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Save, Plus, X, Home, MapPin, CheckSquare } from "lucide-react";
import { IconTextButton } from "../../../components/ButtonComponents";
import { ETabs } from "../../../components/ETabs";
import { Checkbox } from "primereact/checkbox"; // Aktiflik için Checkbox
import { Warehouse } from "../../../types/warehouse";

// Depo tipi
 


// Warehouse tipinden formda gerekmeyen alanları çıkararak state'i belirliyoruz
type WarehouseFormState = Omit<Warehouse, 
    'id' | 'firmId' | 'firm' | 'source' | 'externalId' | 'movements' | 'stockLocations' | 'orders' 
    | 'createdById' | 'createdBy' | 'createdAt' | 'deletedAt' | 'deletedById' | 'deletedBy'
>;

interface WarehouseDialogProps {
  isOpen: boolean;
  onClose: () => void;
  warehouse: Warehouse | null;
  // onSave, Omit edilmiş Warehouse tipini (id hariç) kabul etmelidir.
  onSave: (data: any) => void; 
}

// Initial state
const initialData: WarehouseFormState = {
  name: "",
  address: null, // Modelde String? olduğu için null veya undefined olabilir
  active: true, // Varsayılan olarak aktif başlasın
};


export const WarehouseDialog: React.FC<WarehouseDialogProps> = ({
  isOpen,
  onClose,
  warehouse,
  onSave,
}) => {
  const [formData, setFormData] = useState<WarehouseFormState>(initialData);
  const [activeIndex, setActiveIndex] = useState(0); 

   
useEffect(() => {
  if (!isOpen) return;

  // Modal açıldığında formu doldur
  if (warehouse) {
    setFormData({
      name: warehouse.name || "",
      address: warehouse.address || null,
      active: warehouse.active,
    });
  } else {
    // Yeni kayıt için initial state'e dön
    setFormData(initialData);
  }

  setActiveIndex(0); 

}, [isOpen, warehouse]);

  // FORM HANDLER
  const handleChange = (field: keyof WarehouseFormState, value: string | boolean | null) => {
    setFormData((p) => ({ ...p, [field]: value }));
  };

  
  
const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name) { 
      setActiveIndex(0); 
      console.error("Depo Adı zorunludur.");
      return;
    }
    
    // Payload, WarehouseFormState tipine tam olarak uyuyor.
    const payload: WarehouseFormState = {
        name: formData.name,
        address: formData.address,
        active: formData.active,
    };

    onSave(payload);
    // Dialogu kapatmayı onSave sonrası, hata yoksa, WarehouseList.tsx'teki handleSaveWarehouse fonksiyonu yapıyor.
  };

  // ETabs'a verilecek sekme verileri.
  const tabs = useMemo(() => [
    // 1. SEKME: TEMEL BİLGİLER
    {
      id: 'basic-info',
      header: <><Home className="w-4 h-4 mr-2" /> Temel Bilgiler</>,
      content: (
        <div className="space-y-4 p-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
            {/* 1. Depo Adı */}
            <div className="flex flex-col gap-2">
              <label htmlFor="name">Depo Adı *</label>
              <InputText
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Örn: Merkez Depo"
              />
            </div>
          </div>

          {/* 2. Adres */}
          <div className="flex flex-col gap-2">
            <label htmlFor="address" className="flex items-center gap-1"><MapPin className="w-4 h-4 text-gray-500"/> Adres</label>
            <InputTextarea
              id="address"
              value={formData.address ?? ''}
              onChange={(e) => handleChange('address', e.target.value)}
              rows={3}
              cols={30}
              placeholder="Deponun detaylı adresi"
            />
          </div>
          
          {/* 3. Aktiflik Durumu */}
          <div className="flex items-center gap-2 p-3 card">
             <Checkbox 
                inputId="active" 
                checked={formData.active} 
                onChange={(e) => handleChange('active', e.checked || false)} 
             />
             <label htmlFor="active" className="font-semibold cursor-pointer flex items-center gap-1">
                 <CheckSquare className="w-4 h-4"/> Depo Aktif Mi? (Pasif depolar kullanımdan kaldırılabilir.)
             </label>
          </div>

        </div>
      )
    },
  ], [formData]); 


  return (
    <Dialog
      header={warehouse ? "Depo Düzenle" : "Yeni Depo Ekle"}
      visible={isOpen}
      onHide={onClose}
      modal
        maximizable
      resizable={false}
      className="w-full lg:w-6/12 xl:w-4/12 min-h-[350px]" // Depo formu daha da dar tutulabilir
      footer={
        <div className="flex justify-end gap-3">
          <IconTextButton
            onClick={onClose}
            icon={X}
                 className="btn-cancel"
          >
            İptal
          </IconTextButton>

          <IconTextButton
            onClick={handleSubmit}
            icon={warehouse ? Save : Plus}
            className="btn-new"
          >
            {warehouse ? "Güncelle" : "Depo Ekle"}
          </IconTextButton>
        </div>
      }
    >
      <ETabs 
        tabs={tabs} 
        initialActiveIndex={activeIndex}
        onTabChange={setActiveIndex}
      />

    </Dialog>
  );
};